import { Form, Input, Radio } from 'antd'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { ServerImageInfo } from '@/api/type-server'
import ClearIcon from '@/assets/icons/clear.svg?react'
import { Button, Trans } from '@/components'
import { Modal } from '@/components/base/Modal'
import { buildEnvsString, ImageFormat, toMinResourceNumber } from '@/consts/image'
import {
  getFileSize,
  validateImageFileName,
  validateRawFileName,
  validateTarFileName,
} from '@/utils/file'

import { imageUploaderClient, UploadProgress } from '../image-upload-client'
import {
  generateTaskId,
  ImageRegisterConfig,
  ImageRegistrationResult,
  UploadPausedError,
} from '../types'
import { FileInput } from './FileInput'
import { ImageConfigFields } from './ImageConfigFields'

interface UploadImageModalProps {
  open: boolean
  onClose: () => void
  onUploadStart?: (
    taskId: string,
    fileName: string,
    originalFile?: File,
    originalDescription?: string,
    imageId?: number,
    storeService?: string,
    format?: number,
    config?: ImageRegisterConfig,
  ) => void
  onUploadProgress?: (taskId: string, progress: UploadProgress) => void
  onUploadComplete?: (result: any, taskId: string) => void
  onUploadError?: (taskId: string, error: string) => void
  onUploadPaused?: (taskId: string) => void
  onImageRegistered?: (registrationResult: ImageRegistrationResult) => void
  uploaderInstances?: Map<string, typeof imageUploaderClient>
  imageList: ServerImageInfo[]
  canUploadVm?: boolean
}

export function UploadImageModal({
  open,
  onClose,
  onUploadStart,
  onUploadProgress,
  onUploadComplete,
  onUploadError,
  onUploadPaused,
  onImageRegistered,
  uploaderInstances,
  imageList,
  canUploadVm = false,
}: UploadImageModalProps) {
  const { t } = useTranslation()
  const [form] = Form.useForm()
  const imageType = Form.useWatch('imageType', form)
  const isVm = imageType === 'vm'
  const [fileName, setFileName] = useState('')
  const [fileSize, setFileSize] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [isValidFile, setIsValidFile] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [advancedOpen, setAdvancedOpen] = useState(false)
  const [minResourceOpen, setMinResourceOpen] = useState(false)

  // Build optional registration extras from the form, only including enabled sections.
  // Empty object when both sections are off (harmless to spread into the register payload).
  const buildConfig = (values: any): ImageRegisterConfig => {
    const config: ImageRegisterConfig = {}
    // Advanced fields (envs/cmd/disable_ssh) don't apply at runtime on VM machines, so the section
    // is hidden for VM and never sent — even if it was toggled on before switching to VM.
    if (advancedOpen && !isVm) {
      const envs = buildEnvsString(values.envVars)
      if (envs) config.envs = envs
      const cmd = values.startupCommand?.trim()
      if (cmd) config.cmd = cmd
      config.disable_ssh = values.disableSsh ?? true
    }
    if (minResourceOpen) {
      config.min_resource = {
        cpus: toMinResourceNumber(values.minCpus),
        ramMB: toMinResourceNumber(values.minRamMB),
        diskGB: toMinResourceNumber(values.minDiskGB),
      }
    }
    return config
  }

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0]

      setError(null)
      const lowerCaseFileName = file.name.toLowerCase()

      const extensionValidation = isVm
        ? validateRawFileName(lowerCaseFileName)
        : validateTarFileName(lowerCaseFileName)
      if (!extensionValidation.isValid) {
        setError(
          t(
            isVm
              ? 'customImage.uploadModal.error.invalidRawExtension'
              : 'customImage.uploadModal.error.invalidTarExtension',
          ),
        )
        setIsValidFile(false)
        setSelectedFile(null)
        form.setFieldsValue({ selectedFile: null })
        setFileName('')
        form.setFieldsValue({ imageName: '' })
        setFileSize('')
        return
      }

      const validation = validateImageFileName(lowerCaseFileName)
      if (!validation.isValid) {
        setError(t('customImage.uploadModal.error.invalidFilename'))
        setIsValidFile(false)
        setSelectedFile(null)
        form.setFieldsValue({ selectedFile: null })
        setFileName('')
        form.setFieldsValue({ imageName: '' })
        setFileSize('')
        return
      }

      setIsValidFile(true)
      setSelectedFile(file)
      form.setFieldsValue({ selectedFile: file })
      setFileName(lowerCaseFileName)
      form.setFieldsValue({ imageName: lowerCaseFileName })
      setFileSize(getFileSize(file.size))
    }
  }

  const handleFileDelete = () => {
    setFileName('')
    setFileSize('')
    setError(null)
    setIsValidFile(false)
    setSelectedFile(null)
    form.setFieldsValue({ selectedFile: null })
  }

  const prevImageTypeRef = useRef(imageType)
  useEffect(() => {
    if (prevImageTypeRef.current !== imageType) {
      prevImageTypeRef.current = imageType
      handleFileDelete()
      if (imageType === 'vm') {
        setAdvancedOpen(false)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imageType])

  const checkDuplicateName = (targetName: string): boolean => {
    const lowerTargetName = targetName.toLowerCase()

    return imageList.some((image) => {
      const lowerAlias = (image.alias || '').toLowerCase()
      const lowerName = (image.name || '').toLowerCase()
      return lowerAlias === lowerTargetName || lowerName === lowerTargetName
    })
  }

  const onFinish = async (values: any) => {
    if (!selectedFile) {
      setError(t('customImage.uploadModal.error.noFileSelected'))
      return
    }

    // Check for duplicate names
    if (checkDuplicateName(values.imageName)) {
      setError(t('customImage.uploadModal.error.duplicateName'))
      return
    }

    setIsUploading(true)
    setError(null)

    const taskId = generateTaskId('upload')
    const format = values.imageType === 'vm' ? ImageFormat.VM : ImageFormat.Container
    const config = buildConfig(values)

    if (onUploadStart) {
      onUploadStart(
        taskId,
        values.imageName,
        selectedFile,
        values.description,
        undefined,
        undefined,
        format,
        config,
      )
    }

    // Close modal immediately after starting upload
    onClose()

    // Reset form
    form.resetFields()
    handleFileDelete()
    setAdvancedOpen(false)
    setMinResourceOpen(false)
    setIsUploading(false)

    // Register uploader instance for this task
    if (uploaderInstances) {
      uploaderInstances.set(taskId, imageUploaderClient)
    }

    // Continue upload in background
    try {
      // Create a new File object with lowercase filename
      const fileWithLowerCaseName = new File([selectedFile], values.imageName, {
        type: selectedFile.type,
        lastModified: selectedFile.lastModified,
      })

      const result = await imageUploaderClient.uploadImage(
        fileWithLowerCaseName,
        values.imageName,
        values.description || '',
        format,
        (progress) => {
          // Report progress to parent component
          if (onUploadProgress) {
            onUploadProgress(taskId, progress)
          }
        },
        (registrationResult) => {
          // Notify parent component that image has been registered
          if (onImageRegistered) {
            onImageRegistered(registrationResult)
          }
        },
        config,
      )
      // Notify parent component about completion
      if (onUploadComplete) {
        onUploadComplete(result, taskId)
      }
      // Clean up uploader instance on successful completion
      if (uploaderInstances) {
        uploaderInstances.delete(taskId)
      }
    } catch (error) {
      if (error instanceof UploadPausedError) {
        // Notify parent that upload was paused
        if (onUploadPaused) {
          onUploadPaused(taskId)
        }
        return // Keep instance for resume
      }

      const errorMessage =
        error instanceof Error ? error.message : t('customImage.uploadModal.error.uploadFailed')
      if (onUploadError) {
        onUploadError(taskId, errorMessage)
      }
      // Clean up uploader instance on error (not pause)
      if (uploaderInstances) {
        uploaderInstances.delete(taskId)
      }
    }
  }

  const onFinishFailed = () => {
    // Form validation failed - handled by Ant Design
  }

  return (
    <Modal open={open} onClose={onClose} className="w-[640px]">
      <Modal.Header>
        <Trans i18nKey="customImage.uploadModal.title">Upload Image</Trans>
      </Modal.Header>
      <Modal.Body scrollable showScrollbar>
        {error && (
          <div className="p-4 space-x-4 mb-4 text-error bg-error/10 border border-error/40 rounded-lg flex items-center">
            <ClearIcon
              className="flex-shrink-0 cursor-pointer w-5 h-5"
              onClick={() => setError(null)}
            />
            <span className="t-sm">{error}</span>
          </div>
        )}
        <Form
          form={form}
          layout="vertical"
          name="upload-image-form"
          onFinish={onFinish}
          onFinishFailed={onFinishFailed}
          initialValues={{
            imageType: 'image',
            envVars: [{ key: '', value: '' }],
            disableSsh: true,
          }}
          className="[&>.ant-form-item]:mb-[16px]"
        >
          <Form.Item
            label={
              <div className="t-sb block text-primary">
                <Trans i18nKey="customImage.uploadModal.imageType">Image Type</Trans>
              </div>
            }
            name="imageType"
            rules={[
              { required: true, message: t('customImage.uploadModal.validation.selectImageType') },
            ]}
          >
            <Radio.Group className="flex gap-2">
              <Radio
                value="image"
                className="flex-1 flex items-center px-4 py-3 border rounded-lg t-sm"
              >
                <Trans i18nKey="customImage.uploadModal.imageTypeImage">Image</Trans>
              </Radio>
              <Radio
                value="vm"
                className="flex-1 flex items-center px-4 py-3 border rounded-lg disabled:t-sm disabled:font-medium"
                disabled={!canUploadVm}
              >
                <Trans i18nKey="customImage.uploadModal.imageTypeVM">Virtual Machine</Trans>
              </Radio>
            </Radio.Group>
          </Form.Item>

          <Form.Item
            label={
              <div className="t-sb block text-primary">
                <Trans i18nKey="customImage.uploadModal.imageName">Image Name</Trans>
              </div>
            }
            name="imageName"
            rules={[
              { required: true, message: t('customImage.uploadModal.validation.enterImageName') },
            ]}
          >
            <Input
              className="h-11 px-4 py-3 t-sb placeholder:text-tertiary placeholder:t-sm disabled:bg-panel-white disabled:opacity-60"
              placeholder={t('customImage.uploadModal.imageNamePlaceholder')}
              disabled
            />
          </Form.Item>

          <Form.Item
            label={
              <div className="t-sb block text-primary">
                <Trans i18nKey="customImage.uploadModal.description">Description</Trans>
              </div>
            }
            name="description"
          >
            <Input.TextArea
              className="h-11 px-4 py-3 t-sb placeholder:text-tertiary placeholder:t-sm"
              placeholder={t('customImage.uploadModal.descriptionPlaceholder')}
              rows={3}
            />
          </Form.Item>

          <Form.Item
            label={
              <div className="t-sb block text-primary">
                <Trans i18nKey="customImage.uploadModal.selectImage">Select Image</Trans>
              </div>
            }
            name="selectedFile"
            valuePropName="file"
            rules={[
              { required: true, message: t('customImage.uploadModal.validation.selectImageFile') },
            ]}
          >
            <FileInput
              onChange={handleFileChange}
              fileName={fileName}
              fileSize={fileSize}
              onDelete={handleFileDelete}
              isValidFile={isValidFile}
            />
          </Form.Item>

          <ImageConfigFields
            advancedOpen={advancedOpen}
            onAdvancedOpenChange={setAdvancedOpen}
            minResourceOpen={minResourceOpen}
            onMinResourceOpenChange={setMinResourceOpen}
            showAdvanced={!isVm}
          />
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button type="outline" onClick={onClose} disabled={isUploading}>
          <Trans i18nKey="common.confirmModalCancel">Cancel</Trans>
        </Button>
        <Button
          type="secondary"
          disabled={isUploading || !isValidFile}
          onClick={() => form.submit()}
        >
          <Trans i18nKey="customImage.uploadModal.uploadBtn">
            {isUploading ? 'Starting Upload...' : 'Upload'}
          </Trans>
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
